import {useMemo} from 'react';

import {
  GroupedLeaderboardData,
  LeaderboardValueRecord,
} from '../views/Leaderboard/query/leaderboardQuery';

export interface DatasetInfo {
  id: string;
  name: string;
  datasetName: string;
  datasetVersion: string;
}

/**
 * Calculate the number of unique models selected based on evaluation selections
 */
export const useSelectedModelsCount = (
  data: GroupedLeaderboardData | undefined | null,
  allRecords: LeaderboardValueRecord[],
  selectedEvaluations: string[]
): number => {
  return useMemo(() => {
    if (!data || selectedEvaluations.length === 0) {
      return 0;
    }

    const selectedModelNames = new Set(
      allRecords
        .filter(
          record =>
            record.sourceEvaluationCallId &&
            selectedEvaluations.includes(record.sourceEvaluationCallId)
        )
        .map(record => record.modelName)
    );

    return selectedModelNames.size;
  }, [data, allRecords, selectedEvaluations]);
};

/**
 * Calculate available datasets based on selected evaluations
 */
export const useAvailableDatasets = (
  data: GroupedLeaderboardData | undefined | null,
  allRecords: LeaderboardValueRecord[],
  selectedEvaluations: string[]
): DatasetInfo[] => {
  return useMemo(() => {
    if (!data || selectedEvaluations.length === 0) {
      return [];
    }

    // Get all unique datasets from selected evaluation records
    const datasetMap = new Map<string, DatasetInfo>();

    allRecords
      .filter(
        record =>
          record.sourceEvaluationCallId &&
          selectedEvaluations.includes(record.sourceEvaluationCallId)
      )
      .forEach(record => {
        const key = `${record.datasetName}:${record.datasetVersion}`;
        if (!datasetMap.has(key)) {
          datasetMap.set(key, {
            id: key,
            name: key,
            datasetName: record.datasetName,
            datasetVersion: record.datasetVersion,
          });
        }
      });

    return Array.from(datasetMap.values());
  }, [data, allRecords, selectedEvaluations]);
};
